import './DragManager.scss';
import { BaseClass } from '@components/BaseClass.js';
import { DragFlags } from '@components/DragManager/DragManagerTypes.js';
export class DragManager extends BaseClass {
    constructor() {
        super();
        this.hasCalledMouseDownDelegate = false;
        this.defaultAutoScrollThreshold = 20;
        this.defaultAutoScrollSpeed = 10;
        this.autoScrollTimer = null;
        this.autoScrollDir = { x: 0, y: 0 };
        this.dragInfo = {
            startX: 0,
            startY: 0,
            currentX: 0,
            currentY: 0,
            movedX: 0,
            movedY: 0,
            initialWidth: 0,
            initialHeight: 0,
            initialLeft: 0,
            initialTop: 0,
            initialScrollLeft: 0,
            initialScrollTop: 0,
            dragContainer: undefined,
            dragElement: undefined,
            previewElement: undefined,
            ghostElement: undefined,
            thresholdReached: false,
            initialEvent: undefined,
            computedStyle: undefined,
            lastDropTargetInfo: { target: null, position: 'before' },
            dragOptions: {
                dragType: ''
            }
        };
        this.boundHandleMouseMove = this.handleMouseMove.bind(this);
        this.boundHandleMouseUp = this.handleMouseUp.bind(this);
    }
    pointerListen() {
        console.log('DragManager listening');
        window.addEventListener('pointermove', this.boundHandleMouseMove, { passive: false });
        window.addEventListener('pointerup', this.boundHandleMouseUp);
    }
    pointerUnlisten() {
        console.log('DragManager unlistening');
        window.removeEventListener('pointermove', this.boundHandleMouseMove);
        window.removeEventListener('pointerup', this.boundHandleMouseUp);
    }
    /**
     * Sets up a mouse down event listener on the given component element.
     *
     * @param {Component} component - The component on which the event listeners will be registered.
     * @param {HTMLElement} element - The element on which the mouse down event will be listened for.
     * @param {DragOptions} options - The options for the drag operation.
     * @return {Function} - The event listener function that was registered.
     */
    setupMouseDownEvent(component // the intent of this is for the event listeners to be registered on the component so when it destroys we clean them up	
    , element, options) {
        if (typeof options.autoScroll === 'undefined') {
            options.autoScroll = true;
        }
        if (typeof options.dragStartThreshold === 'undefined') {
            options.dragStartThreshold = DragManager.defaultDragStartThreshold;
        }
        if (!options.dragDelegate && options.dragType == 'move' && options.dragContainer) {
            options.dragDelegate = this.moveContainer.bind(this);
        }
        else if (!options.dragDelegate && options.dragType.indexOf('resize') > -1 && options.dragContainer) {
            options.dragDelegate = this.resizeContainer.bind(this);
        }
        if (options.dragType === 'sort') {
            if (!options.sortType) {
                options.sortType = 'vertical';
            }
            if (!options.dropTargetClass) {
                options.dropTargetClass = 'drop-target';
            }
            if (!options.dropSiblingClass) {
                options.dropSiblingClass = 'drop-sibling';
            }
            if (!options.dropContainerClass) {
                options.dropContainerClass = 'drop-container';
            }
            if (!options.activeDropTargetClass) {
                options.activeDropTargetClass = 'active-drop-target';
            }
            if (!options.siblingThreshold) {
                options.siblingThreshold = 10;
            }
            if (typeof options.previewSorting === 'undefined') {
                options.previewSorting = true;
            }
            if (!options.mouseDownDelegate) {
                options.mouseDownDelegate = this.sortContainerMouseDown.bind(this);
            }
            if (!options.dragDelegate) {
                options.dragDelegate = this.sortContainer.bind(this);
            }
        }
        element.classList.add('draggable');
        const func = (event) => {
            if (!element.classList.contains('draggable')) {
                return;
            }
            /*if (DragFlags.cancelDragMouseDownEvent) {
                DragFlags.cancelDragMouseDownEvent = false;
                return;
            }*/
            if (event._cancelDragMouseDownEvent) {
                return;
            }
            const targetElement = event.target;
            // Check if the element is specifically marked as non-draggable
            if (targetElement.getAttribute('data-draggable') === 'N') {
                return; // Do not initiate drag operations
            }
            // TODO: if we want editable inputs in the form designer to be draggable (by grabbing the input itself not its label), we can add some sort of property to ignore this check 
            // when setting up the drag event in the form designer code. 
            // Check if the target element is an editable input or textarea
            if (targetElement.tagName === 'INPUT' || targetElement.tagName === 'TEXTAREA') {
                // const type = (targetElement!.getAttribute('type') || '').toLowerCase();
                // if (!type || type === 'text' || type === 'password' || type === 'email' || targetElement.tagName === 'TEXTAREA') {
                // 	if (!(targetElement as HTMLInputElement).disabled) { 
                // 		return; // Do not initiate drag operations for editable inputs
                // 	}
                if (!targetElement.classList.contains('disabled')) {
                    return; // Do not initiate drag operations for editable inputs
                }
                // }
            }
            event.preventDefault();
            // this may be a problem because it will stop click actions like clicking a dropdown and we dont even know if they are going to drag enough to pass the threshold.
            // possible solution (make sure it doesnt re-trigger this handler etc):
            // selectElement.dispatchEvent(new MouseEvent('mousedown'));
            // so what we can try is maybe call mousedown on the original targetElement if the drag threshold is never reached 
            // alternatively make our own select component instead of <select> that relies on 'click' instead of 'mousedown', which we almost certainly were going to do anyways. 
            // added to prevent bubbling event up to the parent element. 
            // ie. form designer. We have a drag event on the whole designer container, and also have ones on the resizer elements. 
            // if they use the resizer elements, we dont want the parent drag handler to occur.
            // event.stopImmediatePropagation();
            this.dragInfo.dragOptions = options;
            if (typeof options.dragContainer === 'function') {
                this.dragInfo.dragContainer = options.dragContainer(event, this.dragInfo);
                if (!this.dragInfo.dragContainer) {
                    return;
                }
            }
            else {
                this.dragInfo.dragContainer = options.dragContainer;
            }
            if (typeof options.dragElement === 'function') {
                this.dragInfo.dragElement = options.dragElement(event, this.dragInfo);
                if (!this.dragInfo.dragElement) {
                    return;
                }
            }
            else {
                this.dragInfo.dragElement = options.dragElement || element;
            }
            event.stopImmediatePropagation();
            this.dragInfo.dragElement.classList.add('drag-target');
            let computedStyle;
            if (options.dragType.indexOf('resize') > -1 && this.dragInfo.dragContainer) {
                // currently we only need computedStyle in this scenario so we can see if its a position absolute element.
                computedStyle = window.getComputedStyle(this.dragInfo.dragContainer, null);
            }
            this.dragInfo.computedStyle = computedStyle;
            // If it's a touch event and there are touches available, use the first touch point.
            if (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent && event.touches.length) {
                this.beginDrag(event.touches[0], options);
            }
            // If it's a mouse event, use it directly.
            else if (event instanceof MouseEvent || event instanceof PointerEvent) {
                console.log('beginning drag');
                this.beginDrag(event, options);
            }
        };
        let inputType = options.inputType;
        if (!options.inputType) {
            inputType = 'both';
        }
        const eventName = inputType === 'both' ? 'pointerdown' : (inputType === 'mouse' ? 'mousedown' : 'touchstart');
        return component.registerEvent(eventName, element, func);
    }
    /**
     * When an element starts dragging, it informs the dragManager so that we can have a single mousemove event shared by all elements.
     * @param event
     * @param options
     */
    beginDrag(event, options
    // ,dragType: string
    // ,mouseDownDelegate: Function | null = null
    // ,dragDelegate: Function | null = null
    // ,dropDelegate: Function | null = null
    // ,dragContainer: HTMLElement | null = null
    // ,threshold: number = DragManager.defaultDragStartThreshold
    ) {
        this.clearGhost();
        this.pointerListen();
        this.hasCalledMouseDownDelegate = false;
        // this.dragInfo.startX = mousedownEvent?.clientX || 0; 
        // this.dragInfo.startY = mousedownEvent?.clientY || 0; 
        this.dragInfo.movedX = 0;
        this.dragInfo.movedY = 0;
        this.dragInfo.initialWidth = this.dragInfo.dragContainer?.offsetWidth || 0;
        this.dragInfo.initialHeight = this.dragInfo.dragContainer?.offsetHeight || 0;
        this.dragInfo.initialLeft = this.dragInfo.dragContainer?.offsetLeft || 0;
        this.dragInfo.initialTop = this.dragInfo.dragContainer?.offsetTop || 0;
        this.dragInfo.initialEvent = event;
        this.dragInfo.thresholdReached = options.dragStartThreshold === 0;
        const sc = this.getScrollContainer();
        if (sc) {
            this.dragInfo.scrollContainer = sc;
            this.dragInfo.initialScrollLeft = sc.scrollLeft;
            this.dragInfo.initialScrollTop = sc.scrollTop;
        }
        if (options.resizeWidthProperty === 'flexBasis' && this.dragInfo.dragContainer?.style.flexBasis) {
            this.dragInfo.initialWidth = Number(this.dragInfo.dragContainer?.style.flexBasis.replace('px', ''));
        }
        if ('clientX' in event && 'clientY' in event) {
            this.dragInfo.startX = event.clientX;
            this.dragInfo.startY = event.clientY;
        }
        else {
            this.dragInfo.startX = 0;
            this.dragInfo.startY = 0;
        }
        this.dragInfo.currentX = this.dragInfo.startX;
        this.dragInfo.currentY = this.dragInfo.startY;
        if (options.mouseDownDelegate && this.dragInfo.thresholdReached) {
            DragFlags.isDragging = true;
            options.mouseDownDelegate(event, this.dragInfo);
        }
    }
    /**
     * Moves the container based on mouse event and drag information.
     *
     * @param {MouseEvent} event - The mouse event that triggered the move.
     * @param {DragInfo} dragInfo - The drag information object containing container and movement data.
     */
    moveContainer(event, dragInfo) {
        if (dragInfo.dragContainer) {
            dragInfo.dragContainer.style.left = `${dragInfo.initialLeft + dragInfo.movedX}px`;
            dragInfo.dragContainer.style.top = `${Math.max(0, dragInfo.initialTop + dragInfo.movedY)}px`;
        }
    }
    /**
     * Resizes the container based on the provided drag event and drag information.
     *
     * @param {MouseEvent} event - The mouse event that triggered the drag.
     * @param {DragInfo} dragInfo - The drag information object.
     * @property {HTMLElement} dragInfo.dragContainer - The container element being resized.
     * @property {string} dragInfo.dragOptions.dragType - The type of drag being performed.
     * @property*/
    resizeContainer(event, dragInfo) {
        if (dragInfo.dragContainer) {
            const dragType = dragInfo.dragOptions.dragType;
            const widthProperty = dragInfo.dragOptions.resizeWidthProperty || 'width';
            const acceleration = dragInfo.dragOptions.acceleration || 1;
            if (dragType === 'resize-r' || dragType === 'resize-br') {
                const width = dragInfo.initialWidth + (dragInfo.movedX * acceleration);
                dragInfo.dragContainer.style[widthProperty] = `${width}px`;
            }
            if (dragType === 'resize-l' || dragType === 'resize-bl') {
                const width = dragInfo.initialWidth - (dragInfo.movedX * acceleration);
                const left = dragInfo.initialLeft + (dragInfo.movedX * acceleration);
                dragInfo.dragContainer.style[widthProperty] = `${width}px`;
                if (dragInfo.computedStyle?.position === 'absolute') {
                    dragInfo.dragContainer.style.left = `${left}px`;
                }
            }
            if (dragType === 'resize-b' || dragType === 'resize-br' || dragType === 'resize-bl') {
                const height = dragInfo.initialHeight + (dragInfo.movedY * acceleration);
                dragInfo.dragContainer.style.height = `${height}px`;
            }
        }
    }
    /**
     * Handles the mouse down event on the sort container.
     * Highlights potential drop targets and creates a ghost element if requested.
     *
     * @param {MouseEvent} event - The mouse down event object.
     * @param {DragInfo} dragInfo - The drag information object.
     */
    sortContainerMouseDown(event, dragInfo) {
        if (!dragInfo.dragElement) {
            return;
        }
        if (dragInfo.dragOptions.displayGhostOnDrag && dragInfo.dragElement) {
            this.createGhostElement(dragInfo.dragElement);
            this.highlightDropTargets();
        }
    }
    /**
     * Creates a ghost element based on the provided HTML element.
     *
     * @param {HTMLElement} element - The original HTML element to create a ghost element from.
     * @return {HTMLElement} - The created ghost element.
     */
    createGhostElement(element) {
        let ghost;
        if (this.dragInfo.dragOptions.createGhostElement) {
            ghost = this.dragInfo.dragOptions.createGhostElement(this.dragInfo);
        }
        else {
            ghost = element.cloneNode(true); // Deep clone
            // Get computed style of the original element
            const computedStyle = window.getComputedStyle(element);
            // Set the size of the ghost to match the original element
            ghost.style.width = computedStyle.width;
            ghost.style.height = computedStyle.height;
        }
        ghost.style.position = 'absolute';
        ghost.style.pointerEvents = 'none'; // Ignore pointer events
        ghost.classList.add('ghost-element'); // Add a class for styling
        document.body.appendChild(ghost);
        this.dragInfo.ghostElement = ghost; // Keep a reference to the ghost element
        return ghost;
    }
    /**
     * Highlights the potential drop targets based on the drag options.
     */
    highlightDropTargets() {
        const skipElement = this.dragInfo.dragElement;
        // Highlight container targets
        if (this.dragInfo.dragOptions.containerTargetSelector) {
            const containerTargets = this.dragInfo.dragContainer?.querySelectorAll(this.dragInfo.dragOptions.containerTargetSelector);
            containerTargets?.forEach(target => {
                if (!skipElement || (!skipElement.contains(target) && target !== skipElement && target !== this.dragInfo.previewElement)) {
                    target.classList.add(this.dragInfo.dragOptions.dropTargetClass, this.dragInfo.dragOptions.dropContainerClass);
                }
            });
        }
        // Highlight sibling targets
        if (this.dragInfo.dragOptions.siblingTargetSelector) {
            const siblingTargets = this.dragInfo.dragContainer?.querySelectorAll(this.dragInfo.dragOptions.siblingTargetSelector);
            siblingTargets?.forEach(target => {
                if (!skipElement || (!skipElement.contains(target) && target !== skipElement && target !== this.dragInfo.previewElement)) {
                    target.classList.add(this.dragInfo.dragOptions.dropTargetClass, this.dragInfo.dragOptions.dropSiblingClass);
                }
            });
        }
    }
    /**
     * Clears all drop targets by removing the specified CSS classes from them.
     */
    clearDropTargets() {
        const targets = this.dragInfo.dragContainer?.querySelectorAll(`.${this.dragInfo.dragOptions.dropTargetClass}`);
        targets?.forEach(target => {
            target.classList.remove(this.dragInfo.dragOptions.dropTargetClass, this.dragInfo.dragOptions.activeDropTargetClass, this.dragInfo.dragOptions.activeDropTargetClass + '-before', this.dragInfo.dragOptions.activeDropTargetClass + '-after', this.dragInfo.dragOptions.activeDropTargetClass + '-inside', this.dragInfo.dragOptions.dropContainerClass, this.dragInfo.dragOptions.dropSiblingClass);
        });
    }
    /**
     * Called frequently during sort dragging. Moves the ghost if applicable.
     * Marks the drop target and moves the preview if applicable.
     *
     * @param {MouseEvent} event - The drag event that triggered the sorting.
     * @param {DragInfo} _dragInfo - The drag information containing the dragged element and options.
     */
    sortContainer(event, _dragInfo) {
        if (this.dragInfo.dragElement) {
            if (this.dragInfo.dragOptions.displayGhostOnDrag && this.dragInfo.ghostElement) {
                // Update the position of the ghost element
                this.updateGhostPosition(event);
            }
            this.updateActiveDropTarget();
        }
    }
    /**
     * Updates the position of the ghost element based on the mouse coordinates from the given MouseEvent.
     *
     * @param {MouseEvent} event - The MouseEvent object containing the clientX and clientY properties.
     */
    updateGhostPosition(event) {
        const mouseX = event.clientX;
        const mouseY = event.clientY;
        this.dragInfo.ghostElement.style.left = `${mouseX}px`;
        this.dragInfo.ghostElement.style.top = `${mouseY}px`;
    }
    /**
     * Updates the active drop target based on mouse coordinates.
     * Moves the preview element if applicable.
     */
    updateActiveDropTarget( /*mouseX: number, mouseY: number*/) {
        const skipElement = this.dragInfo.dragElement;
        // Remove active class from any current active target
        const currentActive = this.dragInfo.dragContainer?.querySelector(`.${this.dragInfo.dragOptions.activeDropTargetClass}`);
        if (currentActive) {
            currentActive.classList.remove(this.dragInfo.dragOptions.activeDropTargetClass);
            currentActive.classList.remove(this.dragInfo.dragOptions.activeDropTargetClass + '-before');
            currentActive.classList.remove(this.dragInfo.dragOptions.activeDropTargetClass + '-inside');
            currentActive.classList.remove(this.dragInfo.dragOptions.activeDropTargetClass + '-after');
        }
        let newActive = document.elementFromPoint(this.dragInfo.currentX, this.dragInfo.currentY);
        const closestSibling = this.findClosestSibling(this.dragInfo.currentX, this.dragInfo.currentY, newActive);
        if (closestSibling && this.isSiblingWithinThreshold(closestSibling, this.dragInfo.currentX, this.dragInfo.currentY)) {
            newActive = closestSibling;
        }
        else if (this.dragInfo.dragOptions.containerTargetSelector) {
            const closestContainer = newActive.closest(this.dragInfo.dragOptions.containerTargetSelector);
            if (closestContainer && closestContainer.contains(newActive) || closestContainer === newActive) {
                newActive = closestContainer;
            }
        }
        if (newActive &&
            newActive !== this.dragInfo.previewElement &&
            ((this.dragInfo.dragOptions.containerTargetSelector && newActive.matches(this.dragInfo.dragOptions.containerTargetSelector))
                ||
                    (this.dragInfo.dragOptions.siblingTargetSelector && newActive.matches(this.dragInfo.dragOptions.siblingTargetSelector)))) {
            if (!skipElement || (!skipElement.contains(newActive) && newActive !== skipElement)) {
                newActive.classList.add(this.dragInfo.dragOptions.activeDropTargetClass);
                newActive.classList.add(this.dragInfo.dragOptions.activeDropTargetClass + '-' + this.determineInsertionPosition(newActive, this.dragInfo.currentX, this.dragInfo.currentY));
                if (this.dragInfo.dragOptions.previewSorting) {
                    this.moveComponentPreview(newActive);
                }
            }
        }
    }
    /**
     * Finds the closest sibling element to the given mouse coordinates.
     *
     * @param {number} mouseX - The X coordinate of the mouse.
     * @param {number} mouseY - The Y coordinate of the mouse.
     * @param elementUnderMouse
     * @returns {HTMLElement | null} The closest sibling element or null if no sibling is found.
     */
    findClosestSibling(mouseX, mouseY, elementUnderMouse) {
        if (this.dragInfo.dragOptions.siblingTargetSelector && this.dragInfo.dragContainer) {
            // Find the element under the mouse and its closest container matching containerTargetSelector
            // let elementUnderMouse = document.elementFromPoint(mouseX, mouseY) as HTMLElement;
            let currentContainer = this.dragInfo.dragContainer;
            if (this.dragInfo.dragOptions.containerTargetSelector) {
                currentContainer = elementUnderMouse.closest(this.dragInfo.dragOptions.containerTargetSelector) || this.dragInfo.dragContainer;
            }
            // If there's a current container, proceed to find the closest sibling within this container
            if (currentContainer) {
                const closestSibling = elementUnderMouse.closest(this.dragInfo.dragOptions.siblingTargetSelector);
                if (closestSibling && currentContainer.contains(closestSibling)) {
                    return closestSibling;
                }
                else {
                    const siblings = Array.from(currentContainer.querySelectorAll(this.dragInfo.dragOptions.siblingTargetSelector));
                    let closestSibling = null;
                    let closestDistance = Infinity;
                    siblings.forEach(sibling => {
                        const rect = sibling.getBoundingClientRect();
                        const distance = this.calculateDistanceToElement(rect, mouseX, mouseY);
                        if (distance < closestDistance) {
                            closestDistance = distance;
                            closestSibling = sibling;
                        }
                    });
                    return closestSibling;
                }
            }
        }
        return null;
    }
    /**
     * Calculates the distance from a given point to the center of a rectangle.
     *
     * @param {DOMRect} rect - The rectangle to calculate the distance to.
     * @param {number} mouseX - The x-coordinate of the point.
     * @param {number} mouseY - The y-coordinate of the point.
     * @returns {number} - The distance from the point to the center of the rectangle.
     */
    calculateDistanceToElement(rect, mouseX, mouseY) {
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const dx = mouseX - centerX;
        const dy = mouseY - centerY;
        return Math.sqrt(dx * dx + dy * dy);
    }
    /**
     * Checks if the given element is a sibling within a certain threshold based on the mouse position.
     *
     * @param {HTMLElement} element - The target element to check.
     * @param {number} mouseX - The x-coordinate of the mouse position.
     * @param {number} mouseY - The y-coordinate of the mouse position.
     * @return {boolean} - Returns true if the element is a sibling within the threshold, otherwise false.
     */
    isSiblingWithinThreshold(element, mouseX, mouseY) {
        const threshold = this.dragInfo.dragOptions.siblingThreshold; // pixels, adjust as needed
        const rect = element.getBoundingClientRect();
        const withinHorizontalThreshold = mouseX >= rect.left - threshold && mouseX <= rect.right + threshold;
        const withinVerticalThreshold = mouseY >= rect.top - threshold && mouseY <= rect.bottom + threshold;
        // TODO: we might not care about both horizontal and vertical. Check sortType?
        return withinHorizontalThreshold && withinVerticalThreshold;
    }
    /**
     * Moves the component preview to a new active drop target.
     *
     * @param {HTMLElement} newActiveTarget - The new active target to move the component preview to.
     */
    moveComponentPreview(newActiveTarget) {
        const newPosition = this.determineInsertionPosition(newActiveTarget, this.dragInfo.currentX, this.dragInfo.currentY);
        if (this.dragInfo.lastDropTargetInfo.target !== newActiveTarget || this.dragInfo.lastDropTargetInfo.position !== newPosition) {
            this.dragInfo.lastDropTargetInfo.target = newActiveTarget;
            this.dragInfo.lastDropTargetInfo.position = newPosition;
            const element = this.dragInfo.previewElement || this.dragInfo.dragElement;
            this.positionPreviewRelativeToTarget(element, newActiveTarget, newPosition);
        }
    }
    /**
     * Determines the position for inserting an element based on the target element and mouse coordinates.
     *
     * @param {HTMLElement} targetElement - The target element to determine the insertion position for.
     * @param {number} mouseX - The X-coordinate of the mouse.
     * @param {number} mouseY - The Y-coordinate of the mouse.
     * @returns {'before' | 'after' | 'inside'} - The insertion position relative to the target element.
     */
    determineInsertionPosition(targetElement, mouseX, mouseY) {
        if (!this.dragInfo.dragOptions.siblingTargetSelector) {
            return 'inside';
        }
        const rect = targetElement.getBoundingClientRect();
        const isSiblingTarget = targetElement.matches(this.dragInfo.dragOptions.siblingTargetSelector);
        if (isSiblingTarget) {
            if (['horizontal', 'both'].indexOf(this.dragInfo.dragOptions.sortType) > -1) {
                // Check proximity to left and right borders for horizontal sibling placement
                if (mouseX < rect.left + this.dragInfo.dragOptions.siblingThreshold) {
                    return 'before';
                }
                else if (mouseX > rect.right - this.dragInfo.dragOptions.siblingThreshold) {
                    return 'after';
                }
            }
            // Check proximity to top and bottom borders for vertical sibling placement
            if (mouseY < rect.top + this.dragInfo.dragOptions.siblingThreshold) {
                return 'before';
            }
            else if (mouseY > rect.bottom - this.dragInfo.dragOptions.siblingThreshold) {
                return 'after';
            }
        }
        if (!this.dragInfo.dragOptions.containerTargetSelector || !targetElement.matches(this.dragInfo.dragOptions.containerTargetSelector)) {
            return 'after';
        }
        return 'inside';
    }
    /**
     * Positions the component element relative to the target element based on the given position.
     * Adjusts the styling of the component element for preview purposes.
     *
     * @param {HTMLElement} componentElement - The element to be positioned.
     * @param {HTMLElement} targetElement - The target element relative to which the component element is positioned.
     * @param {'before' | 'after' | 'inside'} position - The position relative to the target element.
     *        Values can be 'before' to insert as a sibling before the target element,
     *        'after' to insert as a sibling after the target element, or
     *        'inside' to insert as a child inside the target element.
     */
    positionPreviewRelativeToTarget(componentElement, targetElement, position) {
        if (position === 'before' || position === 'after') {
            // Insert as a sibling only if targetElement is a valid sibling target
            if (targetElement.matches(this.dragInfo.dragOptions.siblingTargetSelector)) {
                this.previewSiblingDrop(componentElement, targetElement, position);
            }
        }
        else if (position === 'inside') {
            // Insert as a child if targetElement is a valid container
            if (targetElement.matches(this.dragInfo.dragOptions.containerTargetSelector)) {
                this.previewChildDrop(componentElement, targetElement);
            }
        }
        // Adjust styling for preview
        componentElement.classList.add('drop-preview');
    }
    previewChildDrop(dragElement, targetElement) {
        if (this.dragInfo.dragOptions.previewChildDrop) {
            this.dragInfo.dragOptions.previewChildDrop(dragElement, targetElement, this.dragInfo);
        }
        else {
            targetElement.appendChild(dragElement);
        }
    }
    previewSiblingDrop(dragElement, targetElement, position) {
        if (this.dragInfo.dragOptions.previewSiblingDrop) {
            this.dragInfo.dragOptions.previewSiblingDrop(dragElement, targetElement, position, this.dragInfo);
        }
        else {
            const computedStyle = window.getComputedStyle(targetElement);
            const order = computedStyle.order;
            if (position === 'before') {
                targetElement.parentNode?.insertBefore(dragElement, targetElement);
            }
            else {
                targetElement.parentNode?.insertBefore(dragElement, targetElement.nextSibling);
            }
            // Apply the same order to the componentElement if it's not 'auto'
            if (order !== 'auto' && order !== '0') {
                dragElement.style.order = order;
            }
            else if (dragElement.style.order) {
                // remove it as they might be moving the column around between various containers, some that have ord- classes and some that dont
                dragElement.style.order = '';
            }
        }
    }
    /**
     * Cut off drag event early. Automatically called when user releases their mouse after dragging.
     */
    endDrag() {
        // cleanup
        this.clearAutoScrollTimer();
        this.clearGhost();
        this.pointerUnlisten();
        this.dragInfo.dragElement?.classList.remove('drag-target');
        if (this.dragInfo.thresholdReached) {
            if (this.dragInfo.dragOptions.previewSorting) {
                const element = this.dragInfo.previewElement || this.dragInfo.dragElement;
                element?.classList.remove('drop-preview');
                if (this.dragInfo.previewElement) {
                    this.dragInfo.previewElement.remove(); // they previewed a pseudo element instead of the actual dragElement
                }
            }
            if (this.dragInfo.dragOptions.dragType === 'sort') {
                this.clearDropTargets();
            }
        }
        else if (this.dragInfo.initialEvent) {
            // Dispatch a mousedown event to the original event target since they didn't drag enough to reach threshold.
            this.dispatchMousedownEvent(this.dragInfo.initialEvent);
        }
        this.dragInfo.dragOptions = {
            dragType: ''
        };
        this.dragInfo.lastDropTargetInfo.target = null;
        this.dragInfo.dragContainer = undefined;
        this.dragInfo.dragElement = undefined;
        this.dragInfo.previewElement = undefined;
        this.dragInfo.initialEvent = undefined;
        this.dragInfo.thresholdReached = false;
        this.hasCalledMouseDownDelegate = false;
        DragFlags.isDragging = false;
    }
    clearGhost() {
        if (this.dragInfo.ghostElement) {
            this.dragInfo.ghostElement.parentNode?.removeChild(this.dragInfo.ghostElement);
            this.dragInfo.ghostElement = undefined;
        }
    }
    dispatchMousedownEvent(initialEvent) {
        if (!initialEvent.target)
            return;
        // Create a new click event that simulates a user action
        const clickEvent = new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            view: window,
            ...{
                screenX: initialEvent.screenX,
                screenY: initialEvent.screenY,
                clientX: initialEvent.clientX,
                clientY: initialEvent.clientY,
            }
        });
        // DragFlags.cancelDragMouseDownEvent = true;
        clickEvent._cancelDragMouseDownEvent = true;
        // Dispatch the event to the original target
        initialEvent.target.dispatchEvent(clickEvent);
        // setTimeout(() => {
        // 	DragFlags.cancelDragMouseDownEvent = false;
        // }, 0);
    }
    handleMouseUp(e) {
        if (this.dragInfo.thresholdReached) {
            this.dragInfo.dragOptions.dropDelegate?.(e, this.dragInfo);
            // we only want this timestamp to update if an actual drag operation occurred. 
            DragFlags.dragEndTimestamp = new Date().getTime();
        }
        this.endDrag();
    }
    handleMouseMove(event) {
        if (!this.dragInfo.dragOptions.dragType)
            return;
        if (this.dragInfo.dragOptions.inputType === 'mouse' && event.pointerType === 'touch') {
            // required for better touch detection in the chrome devtools mobile emulator. 
            return;
        }
        this.dragInfo.movedX = event.clientX - this.dragInfo.startX;
        this.dragInfo.movedY = event.clientY - this.dragInfo.startY;
        this.dragInfo.currentX = event.clientX;
        this.dragInfo.currentY = event.clientY;
        const b = this.checkDragStartThreshold();
        if (b && this.dragInfo.dragOptions.mouseDownDelegate && !this.hasCalledMouseDownDelegate) {
            this.dragInfo.dragOptions.mouseDownDelegate(this.dragInfo.initialEvent, this.dragInfo);
            this.hasCalledMouseDownDelegate = true;
        }
        // 1) Compute one‐time scroll:
        const { dx, dy } = this.computeAutoScroll(event);
        if (dx || dy) {
            this.doAutoScroll(dx, dy);
        }
        // 2) Manage the continuous timer:
        if (dx || dy) {
            // if direction changed, restart timer
            if (dx !== this.autoScrollDir.x || dy !== this.autoScrollDir.y) {
                this.clearAutoScrollTimer();
                this.autoScrollDir = { x: dx, y: dy };
                this.autoScrollTimer = window.setInterval(() => {
                    this.doAutoScroll(this.autoScrollDir.x, this.autoScrollDir.y);
                }, 50); // every 50ms
            }
        }
        else {
            this.clearAutoScrollTimer();
        }
        if (this.dragInfo.dragOptions.dragDelegate && b) {
            event.preventDefault();
            this.dragInfo.dragOptions.dragDelegate(event, this.dragInfo);
        }
    }
    clearAutoScrollTimer() {
        if (this.autoScrollTimer != null) {
            clearInterval(this.autoScrollTimer);
            this.autoScrollTimer = null;
            this.autoScrollDir = { x: 0, y: 0 };
        }
    }
    /*private handleTouchMove = (event: TouchEvent) => {
        if (!this.dragInfo.dragOptions.dragType) return;
        if (!event.touches.length) return;
        
        const touch = event.touches[0];
        
        this.dragInfo.movedX = touch.clientX - this.dragInfo.startX;
        this.dragInfo.movedY = touch.clientY - this.dragInfo.startY;
        this.dragInfo.currentX = touch.clientX;
        this.dragInfo.currentY = touch.clientY;
        
        const b = this.checkDragStartThreshold();
        if (b && this.dragInfo.dragOptions.mouseDownDelegate && !this.hasCalledMouseDownDelegate) {
            this.dragInfo.dragOptions.mouseDownDelegate(this.dragInfo.initialEvent, this.dragInfo);
            this.hasCalledMouseDownDelegate = true;
        }
        
        if (this.dragInfo.dragOptions.dragDelegate && b) {
            event.preventDefault();
            this.dragInfo.dragOptions.dragDelegate(touch, this.dragInfo);
        }
    }
    
    private handleTouchEnd = (e: TouchEvent) => {
        if (this.dragInfo.thresholdReached) {
            this.dragInfo.dragOptions.dropDelegate?.(e, this.dragInfo);
        }
        this.endDrag();
    }*/
    checkDragStartThreshold() {
        if (this.dragInfo.thresholdReached) {
            return true;
        }
        const movedDistance = Math.sqrt(Math.pow(this.dragInfo.movedX, 2) + Math.pow(this.dragInfo.movedY, 2));
        if (movedDistance >= this.dragInfo.dragOptions.dragStartThreshold) {
            this.dragInfo.thresholdReached = true;
        }
        return this.dragInfo.thresholdReached;
    }
    toggleDraggable(element, draggable = true) {
        if (draggable) {
            element.classList.add('draggable');
        }
        else {
            element.classList.remove('draggable');
        }
    }
    computeAutoScroll(event) {
        const opts = this.dragInfo.dragOptions;
        if (!opts.autoScroll)
            return { dx: 0, dy: 0 };
        const sc = this.getScrollContainer();
        if (!sc)
            return { dx: 0, dy: 0 };
        const r = sc.getBoundingClientRect();
        const t = opts.autoScrollThreshold ?? this.defaultAutoScrollThreshold;
        const s = opts.autoScrollSpeed ?? this.defaultAutoScrollSpeed;
        let dx = 0, dy = 0;
        if (event.clientX < r.left + t)
            dx = -s;
        else if (event.clientX > r.right - t)
            dx = +s;
        if (event.clientY < r.top + t)
            dy = -s;
        else if (event.clientY > r.bottom - t)
            dy = +s;
        // clamp by autoScrollDirection:
        switch (opts.autoScrollDirection ?? 'both') {
            case 'horizontal':
                dy = 0;
                break;
            case 'vertical':
                dx = 0;
                break;
            // 'both' leaves dx,dy
        }
        return { dx, dy };
    }
    doAutoScroll(dx, dy) {
        const sc = this.getScrollContainer();
        sc.scrollBy(dx, dy);
    }
    /** Returns the container to scroll (either explicit or nearest scrollable ancestor) */
    getScrollContainer() {
        const opts = this.dragInfo.dragOptions;
        let el = opts.autoScrollContainer || this.dragInfo.dragContainer;
        while (el && el !== document.body) {
            const style = window.getComputedStyle(el);
            if ((el.scrollWidth > el.clientWidth && /(auto|scroll)/.test(style.overflowX))
                || (el.scrollHeight > el.clientHeight && /(auto|scroll)/.test(style.overflowY))) {
                return el;
            }
            el = el.parentElement;
        }
        return null;
    }
    destroy() {
        this.pointerUnlisten();
        super.destroy();
    }
}
// private static instance: DragManager;
DragManager.defaultDragStartThreshold = 5;
